import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Divider, TextField,
  Autocomplete, Chip, CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  MedicalServices as ProcIcon,
  CalendarToday as CalIcon,
  AccessTime as TimeIcon,
  Timer as DurationIcon,
  Notes as NotesIcon,
  PersonAdd as PersonAddIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DURACOES = ['30 min', '1h 00min', '1h 30min', '2h 00min'];

const ModalAgendamento = ({
  open, onClose, onSave, onBloquear,
  pacientes = [], procedimentos = [], dentistas = [],
  dataDefault = '', horaDefault = '08:00',
  pacienteDefault = '', procedimentoDefault = [], dentistaDefault = '',
  duracaoDefault = '30 min', observacaoDefault = '', modoEdicao = false,
}) => {
  const { isDarkMode } = useAppTheme();

  const [pacienteId, setPacienteId] = useState(pacienteDefault || '');
  const [inputPaciente, setInputPaciente] = useState('');
  const [criandoPaciente, setCriandoPaciente] = useState(false);
  const [pacientesLocais, setPacientesLocais] = useState([]);
  const [dentistaId, setDentistaId] = useState(dentistaDefault || '');
  const [selectedProcs, setSelectedProcs] = useState(
    Array.isArray(procedimentoDefault) ? procedimentoDefault : (procedimentoDefault ? [procedimentoDefault] : [])
  );
  const [data, setData] = useState(dataDefault);
  const [hora, setHora] = useState(horaDefault);
  const [duracao, setDuracao] = useState(duracaoDefault);
  const [observacao, setObservacao] = useState(observacaoDefault);

  // Modo bloqueio — trava a agenda em vez de marcar consulta com paciente
  const [modoBloqueio, setModoBloqueio] = useState(false);
  const [diaTodoBloqueio, setDiaTodoBloqueio] = useState(false);
  const [horaInicioBloqueio, setHoraInicioBloqueio] = useState('08:00');
  const [horaFimBloqueio, setHoraFimBloqueio] = useState('18:00');
  const [motivoBloqueio, setMotivoBloqueio] = useState('');

  React.useEffect(() => {
    if (open) {
      setPacienteId(pacienteDefault || '');
      setInputPaciente('');
      setPacientesLocais([]);
      setDentistaId(dentistaDefault || (dentistas[0]?.id || ''));
      setSelectedProcs(Array.isArray(procedimentoDefault) ? procedimentoDefault : (procedimentoDefault ? [procedimentoDefault] : []));
      setData(dataDefault);
      setHora(horaDefault);
      setDuracao(duracaoDefault || '30 min');
      setObservacao(observacaoDefault || '');
      setModoBloqueio(false);
      setDiaTodoBloqueio(false);
      setHoraInicioBloqueio(horaDefault || '08:00');
      setHoraFimBloqueio('18:00');
      setMotivoBloqueio('');
    }
  }, [open, dataDefault, horaDefault, pacienteDefault, procedimentoDefault, dentistaDefault, dentistas, duracaoDefault, observacaoDefault]);

  const criarPacienteRapido = async (nome) => {
    if (!nome.trim()) return;
    setCriandoPaciente(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_URL}/pacientes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nome: nome.trim(), clinicaId: user.clinicaId, tipoPessoa: 'PF' })
      });
      const novo = await res.json();
      if (novo.id) {
        setPacientesLocais(prev => [...prev, novo]);
        setPacienteId(novo.id);
      }
    } catch {}
    setCriandoPaciente(false);
  };

  const handleAddProc = (id) => {
    if (id && !selectedProcs.includes(id)) setSelectedProcs([...selectedProcs, id]);
  };

  const handleRemoveProc = (id) => setSelectedProcs(selectedProcs.filter(p => p !== id));

  const handleSave = () => {
    if (!pacienteId) { alert('Selecione um paciente antes de salvar!'); return; }
    onSave({ paciente_id: pacienteId, procedimentos: selectedProcs, data, hora, duracao, observacao, user_id: dentistaId });
    onClose();
  };

  const handleBloquear = () => {
    if (!data) { alert('Selecione uma data antes de bloquear!'); return; }
    if (!diaTodoBloqueio && horaFimBloqueio <= horaInicioBloqueio) {
      alert('O horário final precisa ser depois do inicial!');
      return;
    }
    onBloquear({
      user_id: dentistaId,
      data,
      diaTodo: diaTodoBloqueio,
      horaInicio: horaInicioBloqueio,
      horaFim: horaFimBloqueio,
      motivo: motivoBloqueio,
    });
    onClose();
  };

  const pacienteSelecionado = pacientes.find(p => p.id === pacienteId) || null;

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover fieldset': { borderColor: 'rgba(249,115,22,0.5)' },
      '&.Mui-focused fieldset': { borderColor: '#F97316' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#F97316' },
  };

  const paperSx = isDarkMode
    ? { background: 'rgba(30,41,59,0.97)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: '16px' }
    : { background: '#FFFAF5', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '16px' };

  const sectionLabel = (label) => (
    <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {label}
    </Typography>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: paperSx }}>

      {/* Header */}
      <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700} sx={{ borderLeft: '4px solid #F97316', pl: 1.5 }}>
            {modoBloqueio ? 'Bloquear Horário' : modoEdicao ? 'Editar Agendamento' : 'Agendar Consulta'}
          </Typography>
          <Box onClick={onClose} sx={{ cursor: 'pointer', color: 'text.secondary', '&:hover': { color: '#F97316' }, transition: 'color 0.2s' }}>
            <CloseIcon />
          </Box>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: 'rgba(249,115,22,0.15)' }} />

      <DialogContent sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Alternar pra modo bloqueio */}
        {!modoEdicao && (
          <Box>
            {!modoBloqueio ? (
              <Button
                size="small"
                startIcon={<LockIcon sx={{ fontSize: 16 }} />}
                onClick={() => setModoBloqueio(true)}
                sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 0 }}
              >
                Bloquear este horário em vez de agendar
              </Button>
            ) : (
              <Button
                size="small"
                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                onClick={() => setModoBloqueio(false)}
                sx={{ color: '#F97316', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 0 }}
              >
                Voltar a agendar consulta
              </Button>
            )}
          </Box>
        )}

        {/* Paciente */}
        {!modoBloqueio && (
        <Box>
          {sectionLabel('Paciente')}
          <Autocomplete
            key={open ? 'open' : 'closed'}
            options={[...pacientes, ...pacientesLocais.filter(l => !pacientes.find(p => p.id === l.id))]}
            getOptionLabel={p => typeof p === 'string' ? p : (p.nome || '')}
            value={[...pacientes, ...pacientesLocais].find(p => p.id === pacienteId) || null}
            onChange={(_, newVal) => {
              if (newVal?.__criar) { criarPacienteRapido(inputPaciente); }
              else setPacienteId(newVal ? newVal.id : '');
            }}
            inputValue={inputPaciente}
            onInputChange={(_, v) => setInputPaciente(v)}
            filterOptions={(opts, { inputValue }) => {
              const filtrados = opts.filter(o => o.nome?.toLowerCase().includes(inputValue.toLowerCase()));
              if (inputValue.trim() && !filtrados.find(o => o.nome?.toLowerCase() === inputValue.toLowerCase())) {
                filtrados.push({ __criar: true, nome: `Criar "${inputValue.trim()}"`, id: '__criar' });
              }
              return filtrados;
            }}
            size="small"
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.__criar
                  ? <Box display="flex" alignItems="center" gap={1} color="primary.main">
                      <PersonAddIcon fontSize="small" /><Typography variant="body2">{option.nome}</Typography>
                    </Box>
                  : option.nome
                }
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Buscar paciente..." sx={fieldSx}
                InputProps={{ ...params.InputProps, endAdornment: <>{criandoPaciente && <CircularProgress size={16} />}{params.InputProps.endAdornment}</> }}
              />
            )}
            noOptionsText="Nenhum paciente encontrado"
          />
        </Box>
        )}

        {/* Dentista Responsável */}
        {dentistas.length > 1 && (
          <Box>
            {sectionLabel('Dentista Responsável')}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {dentistas.map(d => {
                const ativo = dentistaId === d.id;
                return (
                  <Box
                    key={d.id}
                    onClick={() => setDentistaId(d.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 1.5, py: 0.75, borderRadius: '20px', cursor: 'pointer',
                      border: `1.5px solid ${ativo ? (d.cor || '#F97316') : 'rgba(0,0,0,0.15)'}`,
                      bgcolor: ativo ? `${d.cor || '#F97316'}18` : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: d.cor || '#F97316' }
                    }}
                  >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.cor || '#F97316', flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={ativo ? 700 : 400} sx={{ color: ativo ? (d.cor || '#F97316') : 'text.primary' }}>
                      {d.nome}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Procedimentos */}
        {!modoBloqueio && (
        <Box>
          {sectionLabel('Procedimentos')}
          <Autocomplete
            options={procedimentos.filter(p => !selectedProcs.includes(p.id))}
            getOptionLabel={p => p.nome || ''}
            value={null}
            onChange={(_, newVal) => { if (newVal) handleAddProc(newVal.id); }}
            size="small"
            renderInput={(params) => (
              <TextField {...params} placeholder="Adicionar procedimento..." sx={fieldSx} />
            )}
            noOptionsText="Nenhum procedimento"
          />
          {selectedProcs.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {selectedProcs.map(id => {
                const proc = procedimentos.find(p => p.id === id);
                return (
                  <Chip
                    key={id}
                    label={proc?.nome || id}
                    onDelete={() => handleRemoveProc(id)}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(249,115,22,0.1)',
                      color: '#EA580C',
                      border: '1px solid rgba(249,115,22,0.25)',
                      fontWeight: 600,
                      '& .MuiChip-deleteIcon': { color: '#F97316', '&:hover': { color: '#EA580C' } }
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
        )}

        {/* Data e Hora */}
        <Box sx={{ display: 'grid', gridTemplateColumns: modoBloqueio ? '1fr' : '1fr 1fr', gap: 1.5 }}>
          <Box>
            {sectionLabel('Data')}
            <TextField
              type="date"
              value={data}
              onChange={e => setData(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />
          </Box>
          {!modoBloqueio && (
            <Box>
              {sectionLabel('Hora')}
              <TextField
                type="time"
                value={hora}
                onChange={e => setHora(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            </Box>
          )}
        </Box>

        {/* Campos específicos do bloqueio */}
        {modoBloqueio && (
          <Box>
            <Box
              onClick={() => setDiaTodoBloqueio(v => !v)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none',
                px: 1.5, py: 1, borderRadius: '8px', mb: diaTodoBloqueio ? 0 : 1.5,
                border: `1.5px solid ${diaTodoBloqueio ? '#F97316' : 'rgba(249,115,22,0.2)'}`,
                bgcolor: diaTodoBloqueio ? 'rgba(249,115,22,0.1)' : 'transparent',
              }}
            >
              <Box sx={{
                width: 18, height: 18, borderRadius: '4px', flexShrink: 0,
                border: `2px solid ${diaTodoBloqueio ? '#F97316' : 'rgba(0,0,0,0.3)'}`,
                bgcolor: diaTodoBloqueio ? '#F97316' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12,
              }}>
                {diaTodoBloqueio && '✓'}
              </Box>
              <Typography variant="body2" fontWeight={600} sx={{ color: diaTodoBloqueio ? '#F97316' : 'text.primary' }}>
                Bloquear o dia todo
              </Typography>
            </Box>

            {!diaTodoBloqueio && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                <Box>
                  {sectionLabel('Início')}
                  <TextField
                    type="time"
                    value={horaInicioBloqueio}
                    onChange={e => setHoraInicioBloqueio(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </Box>
                <Box>
                  {sectionLabel('Fim')}
                  <TextField
                    type="time"
                    value={horaFimBloqueio}
                    onChange={e => setHoraFimBloqueio(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            )}

            {sectionLabel('Motivo (opcional)')}
            <TextField
              placeholder="Ex: Almoço, Férias, Compromisso pessoal..."
              value={motivoBloqueio}
              onChange={e => setMotivoBloqueio(e.target.value)}
              size="small"
              fullWidth
              sx={fieldSx}
            />
          </Box>
        )}

        {/* Duração */}
        {!modoBloqueio && (
        <Box>
          {sectionLabel('Duração')}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {DURACOES.map(d => {
              const ativo = duracao === d;
              return (
                <Box
                  key={d}
                  onClick={() => setDuracao(d)}
                  sx={{
                    px: 2, py: 0.75, borderRadius: '20px', cursor: 'pointer',
                    border: `1.5px solid ${ativo ? '#F97316' : 'rgba(249,115,22,0.2)'}`,
                    bgcolor: ativo ? 'rgba(249,115,22,0.1)' : 'transparent',
                    color: ativo ? '#F97316' : 'text.secondary',
                    fontWeight: ativo ? 700 : 400,
                    fontSize: '0.8rem',
                    transition: 'all 0.15s',
                    userSelect: 'none',
                    '&:hover': { borderColor: '#F97316', color: '#F97316' }
                  }}
                >
                  {d}
                </Box>
              );
            })}
          </Box>
        </Box>
        )}

        {/* Observação */}
        {!modoBloqueio && (
        <Box>
          {sectionLabel('Observação')}
          <TextField
            placeholder="Notas adicionais..."
            value={observacao}
            onChange={e => setObservacao(e.target.value)}
            multiline
            rows={2}
            size="small"
            fullWidth
            sx={fieldSx}
          />
        </Box>
        )}

      </DialogContent>

      <Divider sx={{ borderColor: 'rgba(249,115,22,0.15)' }} />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={modoBloqueio ? handleBloquear : handleSave}
          variant="contained"
          disabled={modoBloqueio ? !data : !pacienteId}
          startIcon={modoBloqueio ? <LockIcon /> : <SaveIcon />}
          sx={{
            bgcolor: modoBloqueio ? '#64748b' : '#F97316',
            '&:hover': { bgcolor: modoBloqueio ? '#475569' : '#EA580C' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            boxShadow: modoBloqueio ? 'none' : '0 2px 10px rgba(249,115,22,0.35)',
            borderRadius: '8px', fontWeight: 700,
          }}
        >
          {modoBloqueio ? 'Bloquear' : modoEdicao ? 'Salvar Alterações' : 'Salvar Agendamento'}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default ModalAgendamento;
